import React, { useEffect, useRef, useState } from "react";
import Navbar from "../Navbar";
import { SavedChangesNoticeHost } from "../common/savedChangesNotice";
import OfflineBanner from "../common/offline/OfflineBanner";
import BackOnlineNotice from "../common/offline/BackOnlineNotice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useSubmitCmpBetaFeedbackMutation } from "../../graphql/types";
import { commonStyles } from "../common/commonStyles";
import useStickyTableHeaders from "../../hooks/useStickyTableHeaders";
import CmpIdaChat from "../common/IdaChat/CmpIdaChat";
import { buildStaffErrorFallback, getUserFacingErrorText } from "../../utils/userFacingError";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../utils/apolloCallerContext";

type LayoutProps = {
  children: React.ReactNode;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useStickyTableHeaders(scrollContainerRef);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isNavbarOpen, setIsNavbarOpen] = useState(
    () => window.innerWidth >= 768,
  );
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [idaEnabled, setIdaEnabled] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const navbarDrawerRef = useRef<HTMLDivElement>(null);

  // Feedback shows its own error in the pop-up, so typed text is not lost on /500.
  const [submitCmpBetaFeedbackMutation] = useSubmitCmpBetaFeedbackMutation({
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });

  useEffect(() => {
    const checkIfMobile = () => {
      const isMobileView = window.innerWidth < 768;

      setIsMobile((wasMobile) => {
        if (wasMobile !== isMobileView) {
          setIsNavbarOpen(!isMobileView);
        }

        return isMobileView;
      });
    };

    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    const drawer = navbarDrawerRef.current;

    if (!drawer) {
      return;
    }

    if (isMobile && !isNavbarOpen) {
      drawer.setAttribute("inert", "");
      return;
    }

    drawer.removeAttribute("inert");

    if (!isMobile || !isNavbarOpen) {
      return;
    }

    const mobileMenuButton = mobileMenuButtonRef.current;
    const previousBodyOverflow = document.body.style.overflow;
    const focusableSelector = [
      'a[href]:not([aria-disabled="true"])',
      'button:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(",");

    document.body.style.overflow = "hidden";

    const focusableElements = Array.from(
      drawer?.querySelectorAll<HTMLElement>(focusableSelector) || [],
    ).filter((element) => element.offsetParent !== null);
    focusableElements[0]?.focus();

    const handleDrawerKeyDown = (event: globalThis.KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        !(event.target instanceof Node) ||
        !drawer.contains(event.target)
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        setIsNavbarOpen(false);
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const currentFocusableElements = Array.from(
        drawer?.querySelectorAll<HTMLElement>(focusableSelector) || [],
      ).filter((element) => element.offsetParent !== null);
      const firstElement = currentFocusableElements[0];
      const lastElement =
        currentFocusableElements[currentFocusableElements.length - 1];

      if (!firstElement || !lastElement) {
        event.preventDefault();
        return;
      }

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleDrawerKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener("keydown", handleDrawerKeyDown);
      if (window.innerWidth < 768) {
        mobileMenuButton?.focus();
      }
    };
  }, [isMobile, isNavbarOpen]);

  const handleToggleNavbar = () => {
    setIsNavbarOpen((isOpen) => !isOpen);
  };

  const handleOpenFeedbackModal = () => {
    setIsFeedbackModalOpen(true);
    setSubmitMessage(""); // Clear any previous messages
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
    setFeedbackText("");
    setIsSubmitting(false);
    setSubmitMessage("");
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return;

    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      const result = await submitCmpBetaFeedbackMutation({
        variables: {
          feedback: feedbackText.trim(),
          urlPath: window.location.pathname,
        },
      });

      if (result.data?.submitCMPBetaFeedback?.success) {
        setSubmitMessage(result.data.submitCMPBetaFeedback.message);
        setTimeout(() => {
          handleCloseFeedbackModal();
        }, 2000);
      } else {
        setSubmitMessage(
          result.data?.submitCMPBetaFeedback?.message ||
            buildStaffErrorFallback("send your feedback")
        );
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setSubmitMessage(
        getUserFacingErrorText(error, buildStaffErrorFallback("send your feedback"))
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen bg-white relative overflow-clip">
      {/* Mobile navbar overlay */}
      {isMobile && isNavbarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-20 cursor-default bg-black bg-opacity-50"
          onClick={() => setIsNavbarOpen(false)}
          aria-label="Close navigation menu"
          tabIndex={-1}
        />
      )}

      {/* Navbar */}
      <div
        id="primary-navigation-drawer"
        ref={navbarDrawerRef}
        aria-modal={isMobile && isNavbarOpen ? true : undefined}
        aria-hidden={isMobile && !isNavbarOpen ? true : undefined}
        role={isMobile && isNavbarOpen ? "dialog" : undefined}
        aria-label={isMobile && isNavbarOpen ? "Primary navigation" : undefined}
        className={`${
          isNavbarOpen ? "translate-x-0" : "-translate-x-full"
        } fixed z-30 h-[100dvh] transition-transform duration-300 ease-in-out md:relative md:h-screen`}
      >
        <Navbar
          onClose={isMobile ? () => setIsNavbarOpen(false) : undefined}
        />
      </div>

      {/* Main content */}
      <div ref={scrollContainerRef} data-dashboard-scroll className="min-w-0 flex-1 overflow-auto z-10 transition-all duration-300">
        {/* Mobile toggle button */}
        <button
          ref={mobileMenuButtonRef}
          type="button"
          className={`fixed left-4 top-4 rounded-full bg-white p-2 shadow-md transition-opacity hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 md:hidden ${
            isMobile && isNavbarOpen
              ? "pointer-events-none z-0 opacity-0"
              : "z-40 opacity-100"
          }`}
          onClick={handleToggleNavbar}
          aria-label="Toggle navigation menu"
          aria-controls="primary-navigation-drawer"
          aria-expanded={isNavbarOpen}
          aria-hidden={isMobile && isNavbarOpen ? true : undefined}
          tabIndex={isMobile && isNavbarOpen ? -1 : undefined}
        >
          <FontAwesomeIcon icon={faBars} className="text-gray-700" />
        </button>

        {/* Beta Feedback button - bottom right with platform colors */}
        <button
          className="hidden fixed bottom-6 right-6 z-40 text-white px-4 py-3 rounded-full shadow-lg focus:outline-none focus:ring-2 transition-all duration-200 text-sm font-bold"
          style={{
            backgroundColor: commonStyles.cpaGold,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = commonStyles.cpaGoldDark;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = commonStyles.cpaGold;
          }}
          onClick={handleOpenFeedbackModal}
          aria-label="Send beta feedback"
          tabIndex={0}
          title="Beta Feedback"
        >
          BETA FEEDBACK
        </button>

        {/* Content with padding. Design (CMP Dashboard (20)): 240px bottom
            padding while IDA shows, so content scrolls clear of IDA. */}
        <div
          data-dashboard-content
          className={`md:pt-4 ${isMobile ? "pt-16" : ""} md:ml-0 w-full`}
          style={idaEnabled ? { paddingBottom: 240 } : undefined}
        >
          <OfflineBanner />
          {children}
          <SavedChangesNoticeHost />
          <BackOnlineNotice />
        </div>
        {/* IF11 RP6: IDA lives in the page layer at z-index 15, so every
            in-page dropdown (20+) and every portaled menu or dialog opens
            over it (CMP Dashboard (21) "Pop-ups and action menus sit above IDA"). */}
        <CmpIdaChat onEnabledChange={setIdaEnabled} />
      </div>

      {/* Feedback Modal - moved to root level to cover everything */}
      {isFeedbackModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full relative"
            style={{ zIndex: 10000 }}
          >
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Beta Feedback
              </h3>
              <button
                onClick={handleCloseFeedbackModal}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Close feedback modal"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                We'd love to hear your feedback about Chamber Member Pro. What
                can we improve?
              </p>

              {submitMessage && (
                <div
                  className={`mb-4 p-3 rounded-md text-sm ${
                    submitMessage.includes("successfully")
                      ? "bg-green-100 text-green-800 border border-green-200"
                      : "bg-red-100 text-red-800 border border-red-200"
                  }`}
                >
                  {submitMessage}
                </div>
              )}

              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Type your feedback here..."
                disabled={isSubmitting}
                className="w-full h-32 p-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-cpaGold focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            <div className="flex items-center justify-end p-6 border-t space-x-3">
              <button
                onClick={handleCloseFeedbackModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitFeedback}
                disabled={!feedbackText.trim() || isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                style={{
                  backgroundColor: commonStyles.cpaGold,
                }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor =
                      commonStyles.cpaGoldDark;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor =
                      commonStyles.cpaGold;
                  }
                }}
              >
                {isSubmitting ? "Sending..." : "Send Feedback"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
